import { useActiveOrders } from '../../hooks/useActiveOrders';
import { Config } from '../../config';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { fetchResourceMap, PreloadedData, Res, useApi, useCms } from '../../state/api';
import { ProductContainer } from '../../state/product';
import { useSession } from '../../state/session';
import { UiContainer } from '../../state/ui';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { ActiveOrders } from '../ActiveOrders/ActiveOrders';
import { CheckmarkList } from '../CheckmarkList/CheckmarkList';
import { ContentCard } from '../ContentCard/ContentCard';
import { ButtonLink } from '../Form/Button';
import { HeaderCard } from '../HeaderCard/HeaderCard';
import { Icon, gift } from '../Icon/Icon';
import { InfoCardBoard } from '../InfoCardBoard/InfoCardBoard';
import { Main } from '../Main/Main';
import { ProductListingWithCard } from '../ProductListingWithCard/ProductListingWithCard';
import { Routing } from '../Routes';
import { VerticalSpacer } from '../VerticalSpacer/VerticalSpacer';
import { CmsComponent } from '../cms/CmsComponent';
import { renderTemplate } from '../utils';
import './MyPages.css';
import { getSelectedSpecialPriceProducts } from './SpecialPricesPage';

export const MyPagesPage = (): JSX.Element => {
	const { user } = useSession();
	const { data: bonusBalance } = useApi(Res.bonusBalance, {});
	const { data: orderStats } = useApi(Res.orderStats, {});
	const { data: activeOrders } = useActiveOrders();
	const { data: bonusRegisterData } = useApi(user && !user.bonus ? Res.bonusRegisterData : null, {});
	const { data: superBanner } = useCms(Res.cmsPage, { slug: 'mina-sidor/superbanner' });
	const { data: mostBought } = useApi(user ? Res.mostBought : null, {});

	const { specialPricesIndex } = ProductContainer.useContainer();
	const selectedSpecialProducts = getSelectedSpecialPriceProducts(specialPricesIndex);
	const t = useTranslation().myPages;
	const routes = useTranslation().routes;
	const navT = useTranslation().myPagesNavigation;
	const bonusT = useTranslation().bonusPage;
	const productT = useTranslation().product;
	const { priceWithVat } = UiContainer.useContainer();
	const { asPrice } = useFormatting();
	let firstname = '';
	if (user) firstname = user.firstname;
	const cardTitle = firstname ? `${t.hello} ${firstname}!` : `${t.hello}!`;
	const visibleActiveOrders = activeOrders?.slice(0, 3);
	const visibleMostBought = mostBought?.slice(0, 8);

	const totalOrderVolume = orderStats
		? priceWithVat
			? orderStats.totalExVat + orderStats.vat
			: orderStats.totalExVat
		: 0;
	const bonusRegistrationHeading =
		bonusRegisterData?.bonusAmount && bonusRegisterData.bonusAmount > 0
			? renderTemplate(bonusT.cashInOnPreviousPurchase, {
					// two decimals, and not rounded to nothing: 0,10 was showing as 0
					amount: asPrice(bonusRegisterData.bonusAmount, 2, false),
				})
			: bonusT.becomeBonusCustomerToday;

	return (
		<Main>
			<div id="my-pages-page">
				<AccountPageNavigation />
				<VerticalSpacer spacing="28px" direction="bottom" />
				<HeaderCard
					bannerImage="/images/my-pages-header-image.png"
					heading={cardTitle}
					subheading={t.onMyPagesYouCan}
					listItems={[t.followStats, t.setSpecialPrices, t.subscribeToProducts, t.manageFavorites]}
				/>
				<VerticalSpacer spacing="44px" direction="bottom" />

				{visibleActiveOrders?.length ? (
					<>
						<div className="my-pages-section-heading">
							<div>
								<h2>{t.activeOrders}</h2>
							</div>
							<ButtonLink className="text my-pages-section-link" linkTo={routes.yourOrders}>
								{navT.orders} →
							</ButtonLink>
						</div>
						<VerticalSpacer spacing="24px" mobileSpacing="20px" direction="bottom" />
						{/* TODO: revisit active-order selection logic; for now we show the three latest orders only. */}
						<ActiveOrders orders={visibleActiveOrders} />
						<VerticalSpacer spacing="64px" direction="bottom" />
					</>
				) : null}

				{user && !user.bonus ? (
					<>
						<div className="bonus-register-cards-grid">
							<ContentCard className="register">
								<div className="my-pages-bonus-copy">
									<h2>{bonusT.registerAsBonusCustomer}</h2>
									<p>{bonusT.registerAsBonusCustomerCopy}</p>
									<CheckmarkList className="black compact">
										<li>{bonusT.registerChecklistA}</li>
										<li>{bonusT.registerChecklistB}</li>
										<li>{bonusT.registerChecklistC}</li>
									</CheckmarkList>
								</div>
							</ContentCard>
							<div className="bonus-box">
								<ContentCard className="bonus-wallet">
									<Icon shape={gift} size={100} />
									<h2>{bonusRegistrationHeading}</h2>
								</ContentCard>
								<ButtonLink linkTo={routes.bonus}>{bonusT.becomeBonusCustomer}</ButtonLink>
							</div>
						</div>
						<VerticalSpacer spacing="64px" direction="bottom" />
					</>
				) : null}

				<InfoCardBoard
					bonusBalance={bonusBalance?.notActivatedBonusAmount || 0}
					specialPricesProductCount={selectedSpecialProducts.length}
					totalOrderVolume={totalOrderVolume}
				/>
				<VerticalSpacer spacing="64px" direction="bottom" />

				{superBanner && (
					<>
						<VerticalSpacer spacing="104px" mobileSpacing="96px" direction="bottom" />

						<CmsComponent structure={superBanner} />
					</>
				)}
				{visibleMostBought?.length ? (
					<ProductListingWithCard
						products={visibleMostBought}
						card={
							<ContentCard title={productT.mostBoughtProducts} className="green">
								<></>
							</ContentCard>
						}
					/>
				) : null}
			</div>
		</Main>
	);
};

export const myPagesRouting: Routing = {
	path(urls) {
		return urls.myPages;
	},
	component: MyPagesPage,
	requiresAuth: true,
	preload: async (config: Config): Promise<PreloadedData> => {
		try {
			return await fetchResourceMap(config, Res.cmsPage, { slug: 'mina-sidor/superbanner' }, config.cmsContent);
		} catch {
			return {};
		}
	},
};
